// Ring 1 — single owner of `window.__TEST__`.
//
// `import type Phaser` is allowed (type-only). Runtime-value Phaser
// imports stay out of this module so the seam shape can be validated
// in a Node test environment if needed.
//
// Production-build invariant (T-01-01 mitigation): the entire seam body
// is gated by `TEST_MODE = import.meta.env.DEV || MODE === 'test'`. In
// production builds Vite replaces both expressions with `false`, the
// gate short-circuits at the top of `attach()`, and tree-shaking strips
// the unused branch — so `window.__TEST__` is never defined and the
// `__TEST__` symbol never appears in `dist/`.
//
// JSON-serialization invariant (D-19): `state()` MUST return plain
// JSON-clonable shapes. NEVER `run.player`, `run.player.body`, or
// `RunState.pocket` itself — always shallow-clone to plain objects.

import type Phaser from 'phaser';
import { Rng } from '../utils/random';
import { RunState } from '../run/RunState';
import { PocketSystem } from '../systems/PocketSystem';
import { formatShareSummary } from '../run/shareSummary';
import { resolveShareBaseUrl } from '../data/share';

export type SuitShort = 'h' | 'd' | 'c' | 's';
export type RankShort =
  | 'A' | 'K' | 'Q' | 'J' | 'T'
  | '9' | '8' | '7' | '6' | '5' | '4' | '3' | '2';
export type CardCode = `${RankShort}${SuitShort}`;

export interface RunStateSnapshot {
  scene: string;
  seed: number;
  currentFloor: number;
  pocket: Array<{ rank: RankShort; suit: SuitShort } | null>;
  player: { x: number; y: number; vx: number; vy: number; grounded: boolean };
  cashier?: {
    locked5: Array<{ rank: RankShort; suit: SuitShort }>;
    handName: string;
    score: number;
  };
  // D-TS-02: optional runOver field, only populated when scene === 'RunOverScene'.
  // Base64 dataURL stays OUT — too large for state(), breaks the JSON-serialization
  // budget (D-19 invariant). The dataURL is fetched separately via
  // __TEST__.commands.captureShareCard().
  runOver?: {
    hasShareCard: boolean;
    summaryLength: number;
    summaryPreview: string; // first 60 chars
  };
}

export interface DegenKongTestSeam {
  ready: boolean;
  seed: number | null;
  sceneKey: string | null;
  state(): RunStateSnapshot;
  commands: {
    seed(n: number): void;
    reset(): void;
    forcePocket(cards: CardCode[]): void;
    forcePlayerVelocity(vx: number, vy: number): void;
    teleportPlayer(x: number, y: number): void;
    forceCashierLock(): void;
    // D-TS-01 — Phase 2 additions:
    captureShareCard(): Promise<string>; // base64 dataURL
    getEmojiSummary(): string;
    // WARNING 9 Option A — null-cashier integration seam (Plan 02-5 consumer).
    // Forces RunOverScene without going through RunScene/CashierScene, so
    // RunState.cashier is null and the scene's null-cashier guard fires.
    forceRunOverNoCashier(): void;
  };
}

declare global {
  // eslint-disable-next-line no-var
  interface Window {
    __TEST__?: DegenKongTestSeam;
  }
}

const TEST_MODE = import.meta.env.DEV || import.meta.env.MODE === 'test';

let _seam: DegenKongTestSeam | null = null;

// Reads the live Player from the RunScene if present. Avoids importing
// Phaser values (Ring 1 keeps `import type Phaser`) by treating the
// RunScene as `any` for the player-field access. Casting to `any` is
// localized here and never leaks into call sites.
function getRunScenePlayer(game: Phaser.Game): {
  x: number;
  y: number;
  vx: number;
  vy: number;
  grounded: boolean;
} {
  const runScene = game.scene.getScene('RunScene') as
    | (Phaser.Scene & { player?: { body?: unknown; x: number; y: number } })
    | null;
  const player = runScene?.player;
  if (!player) return { x: 0, y: 0, vx: 0, vy: 0, grounded: false };
  // Phaser Arcade Body shape — accessed structurally so this stays in
  // the type-only import discipline of Ring 1.
  const body = player.body as
    | { velocity: { x: number; y: number }; blocked: { down: boolean }; touching: { down: boolean } }
    | undefined;
  if (!body) return { x: player.x, y: player.y, vx: 0, vy: 0, grounded: false };
  return {
    x: player.x,
    y: player.y,
    vx: body.velocity.x,
    vy: body.velocity.y,
    grounded: body.blocked.down || body.touching.down,
  };
}

// Resolves the live RunScene's player or returns null. Used by the
// forcePlayerVelocity / teleportPlayer commands. console.warn is the
// failure mode (T-01-01: production builds tree-shake the entire seam).
function resolvePlayer(game: Phaser.Game): {
  body: {
    setVelocity: (vx: number, vy: number) => void;
    reset: (x: number, y: number) => void;
  };
  setPosition: (x: number, y: number) => void;
} | null {
  const runScene = game.scene.getScene('RunScene') as
    | (Phaser.Scene & {
        player?: {
          body?: unknown;
          setPosition: (x: number, y: number) => void;
          x: number;
          y: number;
        };
      })
    | null;
  if (!runScene || !runScene.scene.isActive('RunScene')) {
    return null;
  }
  const player = runScene.player;
  if (!player || !player.body) {
    return null;
  }
  return {
    body: player.body as {
      setVelocity: (vx: number, vy: number) => void;
      reset: (x: number, y: number) => void;
    },
    setPosition: (x: number, y: number) => player.setPosition(x, y),
  };
}

function buildSnapshot(game: Phaser.Game): RunStateSnapshot {
  const activeScenes = game.scene.getScenes(true);
  const active = activeScenes[0];

  // D-TS-02: runOver populated only when RunOverScene is the active scene.
  // Structural cast matches the existing getRunScenePlayer / resolvePlayer
  // shape; the runtime accessors live on RunOverScene (Plan 02-3 Task 3.1).
  let runOver: RunStateSnapshot['runOver'] = undefined;
  if (active?.scene.key === 'RunOverScene') {
    const runOverScene = game.scene.getScene('RunOverScene') as
      | (Phaser.Scene & {
          hasShareCardForTest?: () => boolean;
          getEmojiSummaryForTest?: () => string;
        })
      | null;
    const hasShareCard = runOverScene?.hasShareCardForTest?.() ?? false;
    const fullSummary = runOverScene?.getEmojiSummaryForTest?.() ?? '';
    runOver = {
      hasShareCard,
      summaryLength: fullSummary.length,
      summaryPreview: fullSummary.slice(0, 60),
    };
  }

  return {
    scene: active?.scene.key ?? 'unknown',
    seed: Rng.seed,
    currentFloor: RunState.currentFloor,
    pocket: RunState.pocket.map((c) => (c ? { rank: c.rank as RankShort, suit: c.suit as SuitShort } : null)),
    player: getRunScenePlayer(game),
    cashier: RunState.cashier
      ? {
          locked5: RunState.cashier.locked5.map((c) => ({
            rank: c.rank as RankShort,
            suit: c.suit as SuitShort,
          })),
          handName: RunState.cashier.handName,
          score: RunState.cashier.score,
        }
      : undefined,
    runOver,
  };
}

export const TestSeam = {
  attach(game: Phaser.Game, initialSeed: number): void {
    if (!TEST_MODE) return;
    if (typeof window === 'undefined') return;

    const seam: DegenKongTestSeam = {
      ready: false,
      seed: initialSeed,
      sceneKey: null,
      state: () => buildSnapshot(game),
      commands: {
        seed: (n: number) => {
          Rng.set(n);
          seam.seed = n;
        },
        reset: () => {
          RunState.reset();
          game.scene.start('MenuScene');
        },
        // Routes through PocketSystem (HAND-05 sole-writer invariant —
        // never mutate RunState.pocket directly). PocketSystem.clear and
        // tryAdd both emit `pocket:changed`, so the HUD updates on the
        // same code path as in-game pickups (D-18). Reaching past
        // POCKET_CAPACITY=7 is silently dropped by tryAdd's bool return.
        forcePocket: (codes: CardCode[]) => {
          PocketSystem.clear();
          for (const code of codes) {
            const rank = code[0] as RankShort;
            const suit = code[1] as SuitShort;
            PocketSystem.tryAdd({ rank, suit });
          }
        },
        forcePlayerVelocity: (vx: number, vy: number) => {
          const target = resolvePlayer(game);
          if (!target) {
            console.warn(
              '[__TEST__.forcePlayerVelocity] RunScene not active or player not yet spawned.',
              { vx, vy },
            );
            return;
          }
          target.body.setVelocity(vx, vy);
        },
        teleportPlayer: (x: number, y: number) => {
          const target = resolvePlayer(game);
          if (!target) {
            console.warn(
              '[__TEST__.teleportPlayer] RunScene not active or player not yet spawned.',
              { x, y },
            );
            return;
          }
          target.setPosition(x, y);
          target.body.setVelocity(0, 0);
          target.body.reset(x, y);
        },
        // Routes through RunScene.tryEnterCashier so the keyboard path
        // and the test-seam path are bit-for-bit identical (D-18). The
        // intersection-cast (Phaser.Scene & { tryEnterCashier? }) keeps
        // strict-TS happy without `any`; the optional call (?.()) is a
        // belt-and-braces guard against a stale RunScene build that
        // pre-dates Plan 5 Task 5.1.
        forceCashierLock: () => {
          const run = game.scene.getScene('RunScene');
          if (!run || !run.scene.isActive()) {
            console.warn(
              '[__TEST__.forceCashierLock] RunScene is not active.',
            );
            return;
          }
          (run as Phaser.Scene & { tryEnterCashier?: () => void }).tryEnterCashier?.();
        },
        // D-TS-01 — Phase 2 additions. The base64 dataURL is fetched via this
        // intent verb (NOT through state()) so the per-call ~50KB payload
        // never participates in the state-snapshot serialization budget.
        captureShareCard: async (): Promise<string> => {
          const runOver = game.scene.getScene('RunOverScene') as
            | (Phaser.Scene & { captureForTest?: () => Promise<string> })
            | null;
          if (!runOver || !runOver.scene.isActive('RunOverScene')) {
            console.warn('[__TEST__.captureShareCard] RunOverScene is not active.');
            return '';
          }
          return runOver.captureForTest?.() ?? Promise.resolve('');
        },
        getEmojiSummary: (): string => {
          if (!RunState.cashier) {
            console.warn('[__TEST__.getEmojiSummary] no cashier result on RunState.');
            return '';
          }
          return formatShareSummary(RunState.cashier, Rng.seed, resolveShareBaseUrl());
        },
        // WARNING 9 Option A — null-cashier integration coverage for Plan 02-5.
        // Forces RunOverScene without going through RunScene/CashierScene so
        // RunState.cashier is null. RunState.cashier is read-only-from-RunOverScene
        // (CashierScene is the sole writer per Phase-1 invariant); direct clear
        // here is acceptable as a TestSeam privilege under TEST_MODE only.
        forceRunOverNoCashier: (): void => {
          (RunState as unknown as { cashier?: unknown }).cashier = undefined;
          const activeScenes = game.scene.getScenes(true);
          for (const s of activeScenes) {
            if (s.scene.key !== 'RunOverScene') {
              s.scene.stop();
            }
          }
          game.scene.start('RunOverScene');
        },
      },
    };

    _seam = seam;
    window.__TEST__ = seam;
  },

  markReady(): void {
    if (!TEST_MODE) return;
    if (_seam) _seam.ready = true;
  },

  setScene(key: string): void {
    if (!TEST_MODE) return;
    if (_seam) _seam.sceneKey = key;
  },
};
